import { useMemo } from 'react';
import { Address, isAddress } from 'viem';
import { useReadContract } from 'wagmi';

import { useVaultById } from '@/app/components/VaultsPageComponents/hooks/useVaults';
import { DASH } from '@/app/shared/constants/common';
import { Interval } from '@/app/shared/constants/interval';
import { VAULT_TRANCHE, VaultTrancheId } from '@/app/shared/constants/vaults';
import { getTrancheAddressById } from '@/app/shared/constants/web3/chainContracts';
import { ABI } from '@/app/shared/constants/web3/constants';
import { useChainContracts, useVaultChainId } from '@/app/shared/hooks/blockchain/useChainContracts';
import {
  calcClaimableAtUnix,
  formatEstimatedAvailability,
  formatNumber,
  formatUnlockDuration,
} from '@/app/shared/lib/formatters';
import { formatVaultApy } from '@/app/shared/lib/vaultFormatters';

export const useVaultActionDetails = ({
  vaultId,
  trancheId,
  trancheSymbol,
  assetSymbol,
  mzusdSymbol,
  isUsdcDeposit = false,
}: {
  vaultId?: string;
  trancheId: VaultTrancheId;
  trancheSymbol?: string;
  assetSymbol?: string;
  mzusdSymbol?: string;
  isUsdcDeposit?: boolean;
}) => {
  const contracts = useChainContracts(vaultId);
  const chainId = useVaultChainId(vaultId);
  const { vault } = useVaultById(vaultId);

  const trancheAddress = contracts ? getTrancheAddressById(contracts, trancheId) : undefined;
  const vaultAddress = vaultId && isAddress(vaultId) ? (vaultId as Address) : undefined;

  const { data: unstakeDelayEpochs } = useReadContract({
    address: trancheAddress,
    abi: ABI.TRANCHE,
    functionName: 'unstakeDelayEpochs',
    chainId,
    query: {
      enabled: !!trancheAddress && !!chainId,
      staleTime: Interval.ONE_HOUR,
    },
  });

  const { data: epochConfig } = useReadContract({
    address: vaultAddress,
    abi: ABI.VAULT,
    functionName: 'getEpochConfig',
    chainId,
    query: {
      enabled: !!vaultAddress && !!chainId,
      staleTime: Interval.ONE_MIN,
      refetchInterval: Interval.ONE_MIN,
    },
  });

  const { data: pendingEpochDuration } = useReadContract({
    address: vaultAddress,
    abi: ABI.VAULT,
    functionName: 'pendingEpochDuration',
    chainId,
    query: {
      enabled: !!vaultAddress && !!chainId,
      staleTime: Interval.ONE_MIN,
      refetchInterval: Interval.ONE_MIN,
    },
  });

  const { data: withdrawalFeeBps } = useReadContract({
    address: contracts?.MZUSD,
    abi: ABI.MZUSD,
    functionName: 'withdrawalFeeBps',
    chainId,
    query: {
      enabled: !!contracts?.MZUSD && !!chainId,
      staleTime: Interval.ONE_HOUR,
    },
  });

  const epochStart = epochConfig?.[1];
  const epochDuration = epochConfig?.[2];

  const claimableTiming = useMemo(() => {
    if (
      unstakeDelayEpochs === undefined ||
      epochStart === undefined ||
      epochDuration === undefined ||
      pendingEpochDuration === undefined
    ) {
      return undefined;
    }

    return calcClaimableAtUnix({
      epochStartUnix: Number(epochStart),
      epochDurationSeconds: Number(epochDuration),
      pendingEpochDurationSeconds: Number(pendingEpochDuration),
      unstakeDelayEpochs: Number(unstakeDelayEpochs),
    });
  }, [epochDuration, epochStart, pendingEpochDuration, unstakeDelayEpochs]);

  const unlockPeriod = useMemo(() => {
    if (!claimableTiming) return DASH;
    return formatUnlockDuration(claimableTiming.unlockPeriodSeconds);
  }, [claimableTiming]);

  const estimatedAvailability = useMemo(() => {
    if (!claimableTiming) return DASH;
    return formatEstimatedAvailability(claimableTiming.claimableAtUnix);
  }, [claimableTiming]);

  const depositFee = '0%';

  const withdrawFee = useMemo(() => {
    if (withdrawalFeeBps === undefined) return DASH;
    const percent = Number(withdrawalFeeBps) / 100;
    if (percent === 0) return '0%';
    return `${formatNumber(percent, 2, 2)}%`;
  }, [withdrawalFeeBps]);

  const yourApy = useMemo(() => {
    if (!vault) return DASH;
    if (trancheId === VAULT_TRANCHE.SENIOR) return formatVaultApy(vault.trancheSeniorAPY);
    if (trancheId === VAULT_TRANCHE.JUNIOR) return formatVaultApy(vault.trancheJuniorAPY);
    return DASH;
  }, [trancheId, vault]);

  const depositRoute = useMemo(() => {
    const asset = assetSymbol ?? 'wYLDS';
    const mzusd = mzusdSymbol ?? 'mzUSD';
    const tranche = trancheSymbol ?? DASH;
    if (isUsdcDeposit) {
      return `USDC to ${asset} to ${mzusd} to ${tranche}`;
    }
    return `${asset} to ${mzusd} to ${tranche}`;
  }, [assetSymbol, isUsdcDeposit, mzusdSymbol, trancheSymbol]);

  return {
    yourApy,
    unlockPeriod,
    depositRoute,
    depositFee,
    withdrawFee,
    estimatedAvailability,
  };
};
